import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, XCircle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  StatusBadge,
  useToast,
} from '@/components/ui'

interface Submission {
  id: string
  recipientId: string
  bpId: string
  bpName: string
  sapCardCode: string | null
  payload: Record<string, string>
  submittedAt: string
  decision: string
}

const FIELD_LABEL: Record<string, string> = {
  name: 'Firmenname', street: 'Straße', zipCode: 'PLZ', city: 'Stadt',
  country: 'Land', contactPhone: 'Telefon', website: 'Website', industryName: 'Branche',
}

export default function GpInboxPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Submission | null>(null)
  const [rejectNotes, setRejectNotes] = useState('')

  const { data, isLoading, isError, error } = useQuery<Submission[]>({
    queryKey: ['gp-inbox'],
    queryFn: () => apiClient.get('/business-partners/inbox').then((r) => r.data),
  })

  const approveMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/business-partners/inbox/${id}/approve`, {}),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Freigegeben & Writeback angestellt' })
      setSelected(null)
      queryClient.invalidateQueries({ queryKey: ['gp-inbox'] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      apiClient.post(`/business-partners/inbox/${id}/reject`, { notes }),
    onSuccess: () => {
      toast({ tone: 'success', title: 'Abgelehnt' })
      setSelected(null)
      queryClient.invalidateQueries({ queryKey: ['gp-inbox'] })
    },
    onError: (err: Error) => toast({ tone: 'danger', title: 'Fehler', description: err.message }),
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Prüf-Posteingang"
        description="Eingegangene Stammdaten-Updates aus Kampagnen prüfen und freigeben"
        breadcrumbs={[{ label: 'Geschäftspartner', to: '/gp' }, { label: 'Posteingang' }]}
      />

      {isLoading && <LoadingState label="Lade Einreichungen …" />}
      {isError && <ErrorState title="Fehler" description={(error as Error)?.message} />}

      {!isLoading && !isError && (
        <div className="space-y-3">
          {(data ?? []).length === 0 && (
            <EmptyState title="Posteingang leer" description="Keine offenen Einreichungen." />
          )}
          {(data ?? []).map((sub) => (
            <Card key={sub.id}>
              <Card.Body>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-body font-medium text-gray-900">{sub.bpName}</p>
                    {sub.sapCardCode && (
                      <p className="text-small text-gray-500">SAP: {sub.sapCardCode}</p>
                    )}
                    <p className="text-small text-gray-400 mt-0.5">
                      Eingegangen: {new Date(sub.submittedAt).toLocaleString('de-DE')}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(sub.payload).map(([field, val]) => (
                        <span key={field} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          <span className="text-gray-500">{FIELD_LABEL[field] ?? field}:</span>{' '}
                          <span className="text-gray-800 font-medium">{val}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="primary" size="sm" onClick={() => setSelected(sub)}>
                      Prüfen
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}

      {selected && (
        <Modal
          open={!!selected}
          onOpenChange={(v) => { if (!v) setSelected(null) }}
          title={`Einreichung prüfen: ${selected.bpName}`}
        >
          <div className="space-y-4">
            <div>
              <h4 className="text-h4 text-gray-900 mb-2">Eingereichte Daten</h4>
              <div className="space-y-2">
                {Object.entries(selected.payload).map(([field, val]) => (
                  <div key={field} className="flex items-center gap-3">
                    <span className="text-small text-gray-500 w-32 flex-shrink-0">{FIELD_LABEL[field] ?? field}</span>
                    <span className="text-body font-medium text-gray-900">{val}</span>
                    <StatusBadge status="info" label="Neu" />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="text-small font-medium text-gray-700 block mb-1">Ablehnungsgrund (optional)</label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-small resize-none"
                rows={2}
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                placeholder="Begründung für Ablehnung …"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="danger"
                size="sm"
                onClick={() => rejectMutation.mutate({ id: selected.id, notes: rejectNotes })}
                disabled={rejectMutation.isPending}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Ablehnen
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => approveMutation.mutate(selected.id)}
                disabled={approveMutation.isPending}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Freigeben & übernehmen
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
